/* =====================================================================
   QR Code — desenhado aqui, sem biblioteca e sem CDN.
   ---------------------------------------------------------------------
   Mesma decisão do `CodigoDeBarras.tsx`: numa cadeia de suprimentos de
   software de sistema regulado, uma biblioteca a menos é uma superfície a
   menos. O que o QR carrega é a URL pública do ADR-0006, e ela vai
   IMPRESSA — não dá para corrigir depois de colada no tambor.

   Escopo implementado: modo BYTE (ISO/IEC 18004), versões 1 a 10, níveis
   de correção L/M/Q/H, oito máscaras com pontuação de penalidade. É mais
   do que o projeto precisa hoje, e é de propósito: se o domínio ou o
   identificador mudarem de tamanho, o codificador acompanha sem ninguém
   precisar mexer em tabela.

   ⚠️ Este código foi conferido MÓDULO A MÓDULO contra uma implementação
   de referência, e leitor real abriu o resultado. Duas armadilhas estão
   comentadas onde acontecem (a cópia da informação de formato e a regra 3
   da penalidade); as duas produzem um desenho que PARECE um QR e que
   nenhum leitor abre.
   ===================================================================== */

/** Uma célula da matriz: 1 escuro, 0 claro, `null` ainda não decidida. */
type Celula = number | null;
type Matriz = Celula[][];

export type NivelDeCorrecao = "L" | "M" | "Q" | "H";

export interface Qr {
  readonly matriz: readonly number[][];
  readonly versao: number;
  readonly nivel: NivelDeCorrecao;
  readonly mascara: number;
  /** Lado do símbolo em módulos, sem a zona de silêncio. */
  readonly modulos: number;
}

/* --- GF(256), polinômio primitivo 0x11D --------------------------- */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(function preencherTabelas() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255] as number;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[(LOG[a] as number) + (LOG[b] as number)] as number;
}

/** Polinômio gerador de grau `grau` para Reed-Solomon. */
function gerador(grau: number): number[] {
  let g: number[] = [1];
  for (let i = 0; i < grau; i++) {
    const novo: number[] = new Array<number>(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      novo[j] = (novo[j] as number) ^ (g[j] as number);
      novo[j + 1] = (novo[j + 1] as number) ^ gfMul(g[j] as number, EXP[i] as number);
    }
    g = novo;
  }
  return g;
}

/** Codewords de correção de erro para um bloco de dados. */
function corrigir(dados: readonly number[], quantosEC: number): number[] {
  const g = gerador(quantosEC);
  const resto: number[] = new Array<number>(quantosEC).fill(0);
  for (const byte of dados) {
    const fator = byte ^ (resto[0] as number);
    resto.shift();
    resto.push(0);
    if (fator !== 0) {
      for (let i = 0; i < quantosEC; i++) {
        resto[i] = (resto[i] as number) ^ gfMul(g[i + 1] as number, fator);
      }
    }
  }
  return resto;
}

/* --- Tabelas da norma --------------------------------------------- */

/**
 * [EC por bloco, blocos do grupo 1, dados por bloco g1, blocos g2, dados g2].
 *
 * Conferida somando EC×blocos + dados = total de codewords da versão, para as
 * quarenta combinações.
 */
type Blocos = readonly [number, number, number, number, number];

const BLOCOS: Record<NivelDeCorrecao, readonly (Blocos | null)[]> = {
  L: [null,
    [7, 1, 19, 0, 0], [10, 1, 34, 0, 0], [15, 1, 55, 0, 0], [20, 1, 80, 0, 0],
    [26, 1, 108, 0, 0], [18, 2, 68, 0, 0], [20, 2, 78, 0, 0], [24, 2, 97, 0, 0],
    [30, 2, 116, 0, 0], [18, 2, 68, 2, 69]],
  M: [null,
    [10, 1, 16, 0, 0], [16, 1, 28, 0, 0], [26, 1, 44, 0, 0], [18, 2, 32, 0, 0],
    [24, 2, 43, 0, 0], [16, 4, 27, 0, 0], [18, 4, 31, 0, 0], [22, 2, 38, 2, 39],
    [22, 3, 36, 2, 37], [26, 4, 43, 1, 44]],
  Q: [null,
    [13, 1, 13, 0, 0], [22, 1, 22, 0, 0], [18, 2, 17, 0, 0], [26, 2, 24, 0, 0],
    [18, 2, 15, 2, 16], [24, 4, 19, 0, 0], [18, 2, 14, 4, 15], [22, 4, 18, 2, 19],
    [20, 4, 16, 4, 17], [24, 6, 19, 2, 20]],
  H: [null,
    [17, 1, 9, 0, 0], [28, 1, 16, 0, 0], [22, 2, 13, 0, 0], [16, 4, 9, 0, 0],
    [22, 2, 11, 2, 12], [28, 4, 15, 0, 0], [26, 4, 13, 1, 14], [26, 4, 14, 2, 15],
    [24, 4, 12, 4, 13], [28, 6, 15, 2, 16]],
};

/** Centros dos padrões de alinhamento, por versão. */
const ALINHAMENTO: readonly (readonly number[])[] = [[], [], [6, 18], [6, 22], [6, 26],
  [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];

/** Bits de remainder após os codewords, por versão. */
const REMAINDER: readonly number[] = [0, 0, 7, 7, 7, 7, 7, 0, 0, 0, 0];

/** Informação de versão (BCH 18 bits), só a partir da versão 7. */
const INFO_VERSAO: Record<number, number> = { 7: 0x07c94, 8: 0x085bc, 9: 0x09a99, 10: 0x0a4d3 };

/** Indicador do nível na informação de formato. */
const BITS_NIVEL: Record<NivelDeCorrecao, number> = { L: 1, M: 0, Q: 3, H: 2 };

const VERSAO_MAXIMA = 10;

function blocosDe(versao: number, nivel: NivelDeCorrecao): Blocos {
  const tabela = BLOCOS[nivel][versao];
  if (tabela === null || tabela === undefined) {
    throw new Error(`versão ${versao} fora da faixa implementada (1 a ${VERSAO_MAXIMA})`);
  }
  return tabela;
}

/* --- Codificação --------------------------------------------------- */

/** Quantos bytes cabem em modo byte, nesta versão e nível. */
export function capacidadeEmBytes(versao: number, nivel: NivelDeCorrecao): number {
  const [, b1, d1, b2, d2] = blocosDe(versao, nivel);
  const dados = b1 * d1 + b2 * d2;
  const contador = versao <= 9 ? 8 : 16;
  return Math.floor((dados * 8 - 4 - contador) / 8);
}

function menorVersao(quantosBytes: number, nivel: NivelDeCorrecao): number {
  for (let v = 1; v <= VERSAO_MAXIMA; v++) {
    if (capacidadeEmBytes(v, nivel) >= quantosBytes) return v;
  }
  throw new Error(
    `${quantosBytes} bytes não cabem até a versão ${VERSAO_MAXIMA} no nível ${nivel}`,
  );
}

function bytesDe(texto: string): number[] {
  return Array.from(new TextEncoder().encode(texto));
}

/** Monta o fluxo de bits: modo + contador + dados + terminador + padding. */
function fluxoDeDados(bytes: readonly number[], versao: number, nivel: NivelDeCorrecao): number[] {
  const [, b1, d1, b2, d2] = blocosDe(versao, nivel);
  const totalDados = b1 * d1 + b2 * d2;
  const bits: number[] = [];
  const empurrar = (valor: number, quantos: number) => {
    for (let i = quantos - 1; i >= 0; i--) bits.push((valor >> i) & 1);
  };

  empurrar(0b0100, 4);                            // modo byte
  empurrar(bytes.length, versao <= 9 ? 8 : 16);   // contador de caracteres
  for (const b of bytes) empurrar(b, 8);

  // Terminador: até quatro zeros, sem estourar a capacidade.
  const limite = totalDados * 8;
  for (let i = 0; i < 4 && bits.length < limite; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    codewords.push(parseInt(bits.slice(i, i + 8).join(""), 2));
  }
  // Preenchimento alternado, definido pela norma.
  const PAD = [0xec, 0x11] as const;
  let k = 0;
  while (codewords.length < totalDados) codewords.push(PAD[k++ % 2] as number);

  return codewords;
}

/** Divide em blocos, calcula EC e intercala na ordem da norma. */
function intercalar(
  codewords: readonly number[], versao: number, nivel: NivelDeCorrecao,
): number[] {
  const [ec, b1, d1, b2, d2] = blocosDe(versao, nivel);
  const blocosDados: number[][] = [];
  const blocosEC: number[][] = [];
  let p = 0;
  for (let i = 0; i < b1; i++) {
    const bloco = codewords.slice(p, p + d1);
    p += d1;
    blocosDados.push(bloco);
    blocosEC.push(corrigir(bloco, ec));
  }
  for (let i = 0; i < b2; i++) {
    const bloco = codewords.slice(p, p + d2);
    p += d2;
    blocosDados.push(bloco);
    blocosEC.push(corrigir(bloco, ec));
  }

  const saida: number[] = [];
  const maiorDados = Math.max(d1, d2);
  for (let i = 0; i < maiorDados; i++) {
    for (const bloco of blocosDados) if (i < bloco.length) saida.push(bloco[i] as number);
  }
  for (let i = 0; i < ec; i++) {
    for (const bloco of blocosEC) saida.push(bloco[i] as number);
  }
  return saida;
}

/* --- Matriz -------------------------------------------------------- */

const linhaDe = (m: Matriz, i: number): Celula[] => m[i] as Celula[];
const em = (m: Matriz, i: number, j: number): Celula => linhaDe(m, i)[j] as Celula;
const por = (m: Matriz, i: number, j: number, valor: Celula): void => {
  linhaDe(m, i)[j] = valor;
};

function novaMatriz(tamanho: number): Matriz {
  const m: Matriz = [];
  for (let i = 0; i < tamanho; i++) m.push(new Array<Celula>(tamanho).fill(null));
  return m;
}

function porFinder(m: Matriz, linha: number, coluna: number): void {
  const n = m.length;
  for (let i = -1; i <= 7; i++) {
    for (let j = -1; j <= 7; j++) {
      const y = linha + i;
      const x = coluna + j;
      if (y < 0 || y >= n || x < 0 || x >= n) continue;
      const dentro = i >= 0 && i <= 6 && j >= 0 && j <= 6;
      const anel = dentro && (i === 0 || i === 6 || j === 0 || j === 6);
      const miolo = dentro && i >= 2 && i <= 4 && j >= 2 && j <= 4;
      por(m, y, x, anel || miolo ? 1 : 0);
    }
  }
}

function porAlinhamento(m: Matriz, versao: number): void {
  const centros = ALINHAMENTO[versao] ?? [];
  for (const cy of centros) {
    for (const cx of centros) {
      // Não sobrepõe os finders.
      if (em(m, cy, cx) !== null) continue;
      for (let i = -2; i <= 2; i++) {
        for (let j = -2; j <= 2; j++) {
          const borda = Math.abs(i) === 2 || Math.abs(j) === 2;
          const centro = i === 0 && j === 0;
          por(m, cy + i, cx + j, borda || centro ? 1 : 0);
        }
      }
    }
  }
}

function porTiming(m: Matriz): void {
  const n = m.length;
  for (let i = 8; i < n - 8; i++) {
    const v = i % 2 === 0 ? 1 : 0;
    if (em(m, 6, i) === null) por(m, 6, i, v);
    if (em(m, i, 6) === null) por(m, i, 6, v);
  }
}

/** Marca as posições reservadas para formato e versão (ainda sem valor). */
function reservar(m: Matriz, versao: number): void {
  const n = m.length;
  for (let i = 0; i < 9; i++) {
    if (em(m, 8, i) === null) por(m, 8, i, 2);
    if (em(m, i, 8) === null) por(m, i, 8, 2);
  }
  for (let i = 0; i < 8; i++) {
    if (em(m, 8, n - 1 - i) === null) por(m, 8, n - 1 - i, 2);
    if (em(m, n - 1 - i, 8) === null) por(m, n - 1 - i, 8, 2);
  }
  por(m, n - 8, 8, 1); // módulo escuro, fixo pela norma
  if (versao >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        por(m, n - 11 + j, i, 2);
        por(m, i, n - 11 + j, 2);
      }
    }
  }
}

/** Zigue-zague de duas colunas, de baixo para cima, pulando a coluna 6. */
function posicionarDados(m: Matriz, bits: readonly number[]): void {
  const n = m.length;
  let bit = 0;
  let subindo = true;
  for (let par = n - 1; par > 0; par -= 2) {
    if (par === 6) par = 5; // a coluna 6 é o timing vertical
    for (let passo = 0; passo < n; passo++) {
      const y = subindo ? n - 1 - passo : passo;
      for (const x of [par, par - 1]) {
        if (em(m, y, x) !== null) continue;
        por(m, y, x, bit < bits.length ? (bits[bit] as number) : 0);
        bit++;
      }
    }
    subindo = !subindo;
  }
}

type Mascara = (i: number, j: number) => boolean;

const MASCARAS: readonly Mascara[] = [
  (i, j) => (i + j) % 2 === 0,
  (i) => i % 2 === 0,
  (_i, j) => j % 3 === 0,
  (i, j) => (i + j) % 3 === 0,
  (i, j) => (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0,
  (i, j) => ((i * j) % 2) + ((i * j) % 3) === 0,
  (i, j) => ((((i * j) % 2) + ((i * j) % 3)) % 2) === 0,
  (i, j) => ((((i + j) % 2) + ((i * j) % 3)) % 2) === 0,
];

function aplicarMascara(
  matriz: Matriz, funcao: Mascara, reservado: readonly (readonly boolean[])[],
): Matriz {
  const n = matriz.length;
  const m: Matriz = matriz.map((linha) => linha.slice());
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if ((reservado[i] as readonly boolean[])[j] === true) continue;
      if (funcao(i, j)) por(m, i, j, (em(m, i, j) as number) ^ 1);
    }
  }
  return m;
}

function penalidade(m: Matriz): number {
  const n = m.length;
  let total = 0;

  // Regra 1: sequências de cinco ou mais módulos iguais.
  for (let i = 0; i < n; i++) {
    for (const eixo of [0, 1]) {
      let corrida = 1;
      for (let j = 1; j < n; j++) {
        const atual = eixo === 0 ? em(m, i, j) : em(m, j, i);
        const anterior = eixo === 0 ? em(m, i, j - 1) : em(m, j - 1, i);
        if (atual === anterior) {
          corrida++;
        } else {
          if (corrida >= 5) total += 3 + (corrida - 5);
          corrida = 1;
        }
      }
      if (corrida >= 5) total += 3 + (corrida - 5);
    }
  }

  // Regra 2: blocos 2×2 da mesma cor.
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - 1; j++) {
      const v = em(m, i, j);
      if (v === em(m, i, j + 1) && v === em(m, i + 1, j) && v === em(m, i + 1, j + 1)) {
        total += 3;
      }
    }
  }

  // Regra 3: o padrão 1:1:3:1:1 com quatro claros de um dos lados.
  //
  // ⚠️ O QUE FICA FORA DO SÍMBOLO CONTA COMO CLARO. É o ponto em que uma
  // leitura ingênua da tabela erra: um padrão encostado na borda tem os quatro
  // claros do lado de fora — e é justamente ali que ele imita um finder para o
  // leitor, que enxerga o papel em volta. Ignorar a borda fazia este
  // codificador escolher outra máscara que a implementação de referência, em
  // dois terços dos casos.
  const ALVO = [1, 0, 1, 1, 1, 0, 1] as const;
  const casa = (seq: readonly Celula[], k: number) => ALVO.every((v, d) => seq[k + d] === v);
  /** Há algum módulo escuro em [de, ate)? Fora do símbolo é claro. */
  const escuroEntre = (seq: readonly Celula[], de: number, ate: number) => {
    for (let k = Math.max(de, 0); k < Math.min(ate, n); k++) {
      if (seq[k] === 1) return true;
    }
    return false;
  };
  for (let i = 0; i < n; i++) {
    const linha = linhaDe(m, i);
    const coluna = m.map((l) => l[i] as Celula);
    for (const seq of [linha, coluna]) {
      let j = 0;
      while (j + 7 <= n) {
        if (!casa(seq, j)) {
          j++;
          continue;
        }
        const antes = !escuroEntre(seq, j - 4, j);
        const depois = !escuroEntre(seq, j + 7, j + 11);
        if (antes || depois) {
          total += 40;
          j += 7;
        } else {
          // Sem folga clara dos dois lados: o próximo casamento possível começa
          // no terceiro módulo escuro deste.
          j += 4;
        }
      }
    }
  }

  // Regra 4: desvio da proporção de 50% de módulos escuros.
  let escuros = 0;
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) escuros += em(m, i, j) as number;
  const percentual = (escuros * 100) / (n * n);
  total += Math.floor(Math.abs(percentual - 50) / 5) * 10;

  return total;
}

function bitsDeFormato(nivel: NivelDeCorrecao, mascara: number): number {
  const dados = (BITS_NIVEL[nivel] << 3) | mascara;
  let valor = dados << 10;
  for (let i = 4; i >= 0; i--) {
    if (valor & (1 << (i + 10))) valor ^= 0x537 << i;
  }
  return ((dados << 10) | valor) ^ 0x5412;
}

function gravarFormato(m: Matriz, nivel: NivelDeCorrecao, mascara: number): void {
  const n = m.length;
  const bits = bitsDeFormato(nivel, mascara);
  const bit = (k: number) => (bits >> k) & 1;

  // ⚠️ Linha e coluna NÃO são intercambiáveis aqui, e o erro é silencioso:
  // trocadas, o desenho continua parecendo um QR — só que com o nível e a
  // máscara errados no cabeçalho, e nenhum leitor abre. Foi assim que este
  // codificador errou na primeira volta, e o que apontou foi a conferência
  // módulo a módulo contra uma implementação de referência.
  //
  // Cópia vertical, descendo pelo finder superior-esquerdo.
  for (let i = 0; i <= 5; i++) por(m, i, 8, bit(i));
  por(m, 7, 8, bit(6));
  por(m, 8, 8, bit(7));
  por(m, 8, 7, bit(8));
  for (let i = 9; i <= 14; i++) por(m, 8, 14 - i, bit(i));

  // Cópia partida entre o finder inferior-esquerdo e o superior-direito.
  for (let i = 0; i <= 7; i++) por(m, 8, n - 1 - i, bit(i));
  for (let i = 8; i <= 14; i++) por(m, n - 15 + i, 8, bit(i));

  por(m, n - 8, 8, 1);
}

function gravarVersao(m: Matriz, versao: number): void {
  if (versao < 7) return;
  const n = m.length;
  const bits = INFO_VERSAO[versao] as number;
  for (let i = 0; i < 18; i++) {
    const b = (bits >> i) & 1;
    const linha = Math.floor(i / 3);
    const coluna = i % 3;
    por(m, n - 11 + coluna, linha, b);
    por(m, linha, n - 11 + coluna, b);
  }
}

/**
 * Gera a matriz do QR.
 *
 * @param texto  o conteúdo — aqui, a URL pública do ADR-0006
 * @param nivel  L | M | Q | H
 * @param versao fixa a versão; se omitida, usa a menor que couber
 */
export function gerarQr(
  texto: string, nivel: NivelDeCorrecao = "Q", versao: number | null = null,
): Qr {
  const bytes = bytesDe(texto);
  const v = versao ?? menorVersao(bytes.length, nivel);
  if (bytes.length > capacidadeEmBytes(v, nivel)) {
    throw new Error(`${bytes.length} bytes não cabem na versão ${v} nível ${nivel}`);
  }

  const codewords = intercalar(fluxoDeDados(bytes, v, nivel), v, nivel);
  const bits: number[] = [];
  for (const c of codewords) for (let i = 7; i >= 0; i--) bits.push((c >> i) & 1);
  for (let i = 0; i < (REMAINDER[v] ?? 0); i++) bits.push(0);

  const n = v * 4 + 17;
  const m = novaMatriz(n);
  porFinder(m, 0, 0);
  porFinder(m, 0, n - 7);
  porFinder(m, n - 7, 0);
  porAlinhamento(m, v);
  porTiming(m);
  reservar(m, v);

  // Tudo que não é dado é função: guarda-se o mapa antes de posicionar.
  const reservado: boolean[][] = m.map((linha) => linha.map((c) => c !== null));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) if (em(m, i, j) === 2) por(m, i, j, 0);
  }

  posicionarDados(m, bits);

  let melhor: Matriz | null = null;
  let melhorNota = Infinity;
  let melhorMascara = 0;
  for (let k = 0; k < 8; k++) {
    const candidato = aplicarMascara(m, MASCARAS[k] as Mascara, reservado);
    gravarFormato(candidato, nivel, k);
    gravarVersao(candidato, v);
    const nota = penalidade(candidato);
    if (nota < melhorNota) {
      melhorNota = nota;
      melhor = candidato;
      melhorMascara = k;
    }
  }

  return {
    matriz: (melhor as Matriz) as number[][],
    versao: v,
    nivel,
    mascara: melhorMascara,
    modulos: n,
  };
}

/**
 * O QR como um único `<path>` — um `M`/`h`/`v`/`z` por módulo escuro.
 *
 * <p>Menos nós que um `<rect>` por módulo e nenhuma costura entre módulos
 * vizinhos. Costura é o que produz linha branca de 0,1 px no rasterizador, e
 * linha branca no meio de um módulo é falha de leitura.
 */
export function caminhoDoQr(qr: Qr, silencio: number): string {
  const n = qr.modulos;
  const partes: string[] = [];
  for (let i = 0; i < n; i++) {
    const linha = qr.matriz[i] as readonly number[];
    for (let j = 0; j < n; j++) {
      if (linha[j] === 1) partes.push(`M${j + silencio} ${i + silencio}h1v1h-1z`);
    }
  }
  return partes.join("");
}
